import { ApButtonGroup, ApButtonTab } from '@alixpartners/ui-components';
import { ClaimEditValues } from 'api/endpoints';
import classNames from 'classnames';
import 'components/UIComponents/ui-button-reset.css';
import { useClaimAttachmentsContext } from 'pages/SingleContractPage/context/ClaimAttachmentsProvider';
import { useCallback, useMemo } from 'react';
import { useSingleClaimMatches } from '../sections/SingleClaimMatches/useSingleClaimMatches';
import { SingleClaimSubheaderType } from '../types';
import './SingleClaimSubheader.css';

export interface SingleClaimSubheaderProps {
  claim: ClaimEditValues;
  tab: SingleClaimSubheaderType;
  onTabClick: (s: SingleClaimSubheaderType) => unknown;
  relatedClaimsCount: number;
}

const SINGLE_CLAIM_SUBHEADER_OPTIONS = [
  {
    type: SingleClaimSubheaderType.Overview,
    label: 'Overview',
    hidden: false,
  },
  {
    type: SingleClaimSubheaderType.Matches,
    label: 'Matches',
    hidden: false,
  },
  {
    type: SingleClaimSubheaderType.ChangesAndComments,
    label: 'Changes & Comments',
    hidden: false,
  },
  {
    type: SingleClaimSubheaderType.Documents,
    label: 'Documents',
    hidden: false,
  },
  {
    type: SingleClaimSubheaderType.RelatedClaims,
    label: 'Related Claims',
    hidden: false,
  },
];

export const SingleClaimSubheader = ({
  tab,
  relatedClaimsCount,
  onTabClick,
}: SingleClaimSubheaderProps) => {
  const { matchedClaims } = useSingleClaimMatches();
  const { attachments } = useClaimAttachmentsContext();

  const matchesCount = useMemo(
    () =>
      matchedClaims && matchedClaims?.length > 0 ? ` [${matchedClaims.length}]` : '',
    [matchedClaims],
  );

  const documentsCount = useMemo(
    () => (attachments.length > 0 ? ` [${attachments.length}]` : ''),
    [attachments],
  );

  const labelGenerator = useCallback(
    (s: { label: string; type: SingleClaimSubheaderType }) => {
      switch (s.type) {
        case SingleClaimSubheaderType.Documents:
          return `${s.label}${documentsCount}`;
        case SingleClaimSubheaderType.Matches:
          return `${s.label}${matchesCount}`;
        case SingleClaimSubheaderType.RelatedClaims:
          return `${s.label} ${relatedClaimsCount ? `[${relatedClaimsCount}]` : ''}`;
        default:
          return s.label;
      }
    },
    [documentsCount, matchesCount, relatedClaimsCount],
  );

  return (
    <div className="single-claim-subheader">
      <div className="single-claim-subheader__tabs">
        <ApButtonGroup justifyContent="initial" spacingSize={0}>
          {SINGLE_CLAIM_SUBHEADER_OPTIONS.map((s) => {
            let isTabDisabled = false;

            if (s.type === SingleClaimSubheaderType.Matches)
              isTabDisabled = matchedClaims?.length === 0;
            if (s.type === SingleClaimSubheaderType.RelatedClaims)
              isTabDisabled = relatedClaimsCount === 0;

            const label = labelGenerator(s);

            return (
              <ApButtonTab
                key={s.type}
                className={classNames('single-claim-subheader__tab', {
                  selected: tab === s.type,
                })}
                autoFocus={false}
                isSelected={tab === s.type}
                onClick={() => onTabClick(s.type)}
                theme="light"
                disabled={isTabDisabled}
              >
                {label}
              </ApButtonTab>
            );
          })}
        </ApButtonGroup>
      </div>
    </div>
  );
};
